import React, { useRef, useState } from 'react';
import { useToolStore } from '../../store/useToolStore';
import { Plus, Trash2, MapPin, Upload, Image as ImageIcon } from 'lucide-react';

export const LandscapeIndex: React.FC = () => {
    const { lensParams, setLensParams } = useToolStore();
    const [mapImage, setMapImage] = useState<string | null>(null);
    const [activeBuildingId, setActiveBuildingId] = useState<string | null>(null);
    const [placingPinId, setPlacingPinId] = useState<string | null>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    const buildings = lensParams.buildings || [];

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setMapImage(event.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!imgRef.current || !placingPinId) return;

        const rect = imgRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        if (x >= 0 && x <= 1 && y >= 0 && y <= 1) {
            const newBuildings = buildings.map(b => b.id === placingPinId ? { ...b, x, y } : b);
            setLensParams({ buildings: newBuildings });
            setPlacingPinId(null);
        }
    };

    const addBuilding = () => {
        const nextPin = buildings.length + 1;
        const newBuilding = {
            id: `bld-${Date.now()}`,
            pinLabel: String(nextPin),
            name: '',
            description: '',
        };
        setLensParams({ buildings: [...buildings, newBuilding] });
        setActiveBuildingId(newBuilding.id);
        setPlacingPinId(null);
    };

    const removeBuilding = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const newBuildings = buildings.filter(b => b.id !== id);
        setLensParams({ buildings: newBuildings });
        if (activeBuildingId === id) setActiveBuildingId(null);
        if (placingPinId === id) setPlacingPinId(null);
    };

    const updateBuilding = (id: string, updates: any) => {
        const newBuildings = buildings.map(b => b.id === id ? { ...b, ...updates } : b);
        setLensParams({ buildings: newBuildings });
    };

    return (
        <div className="space-y-4">
            <p className="text-xs text-zinc-400">Map your site plan to ground the AI's understanding of the layout.</p>
            
            {/* Image Map Area */}
            <div className="border border-zinc-800 rounded-lg overflow-hidden bg-zinc-900 relative min-h-[150px] flex items-center justify-center">
                {mapImage ? (
                    <div 
                        className={`relative w-full transition-transform ${placingPinId ? 'cursor-crosshair ring-2 ring-amber-500' : 'cursor-default'}`}
                        onClick={handleCanvasClick}
                    >
                        <img 
                            ref={imgRef}
                            src={mapImage} 
                            alt="Site Plan" 
                            className="w-full object-contain"
                            draggable={false}
                        />
                        {/* Render Pins */}
                        {buildings.map(b => b.x !== undefined && b.y !== undefined && (
                            <div 
                                key={b.id}
                                className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300 ease-out z-10`}
                                style={{ left: `${b.x * 100}%`, top: `${b.y * 100}%` }}
                                onClick={(e) => { 
                                    if (!placingPinId) {
                                        e.stopPropagation(); 
                                        setActiveBuildingId(b.id); 
                                    }
                                }}
                            >
                                <div className={`w-6 h-6 flex items-center justify-center bg-black text-white rounded-md text-xs font-bold shadow-lg ring-1 ${activeBuildingId === b.id ? 'ring-emerald-500 scale-110' : 'ring-white/30'} cursor-pointer`}>
                                    {b.pinLabel}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <label className="flex flex-col items-center justify-center p-6 cursor-pointer hover:bg-zinc-800/50 transition-colors w-full h-full">
                        <ImageIcon className="w-8 h-8 text-zinc-500 mb-2" />
                        <span className="text-sm font-medium text-zinc-300">Upload Site Plan</span>
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                    </label>
                )}
                {placingPinId && mapImage && (
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-amber-500 text-black text-[10px] font-bold px-3 py-1.5 rounded shadow-lg pointer-events-none z-20">
                        Click on map to place pin
                    </div>
                )}
                {mapImage && !placingPinId && (
                     <label className="absolute bottom-2 right-2 bg-zinc-800/80 hover:bg-zinc-700 text-white text-[10px] px-2 py-1 rounded backdrop-blur cursor-pointer transition-colors">
                        Change Image
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                     </label>
                )}
            </div>

            {/* Building List */}
            <div className="space-y-2">
                <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Features Index</span>
                    <button onClick={addBuilding} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                        <Plus size={12} /> Add
                    </button>
                </div>
                
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                    {buildings.map((b) => {
                        const isPinned = b.x !== undefined && b.y !== undefined;
                        const isPlacing = placingPinId === b.id;
                        const isActive = activeBuildingId === b.id;

                        return (
                            <div key={b.id} className={`bg-zinc-900 border rounded-lg p-2 transition-colors ${isActive ? 'border-indigo-500/50' : 'border-zinc-800 hover:border-zinc-700'}`}>
                                <div className="flex items-center gap-2 cursor-pointer" onClick={() => setActiveBuildingId(isActive ? null : b.id)}>
                                    <input
                                        type="text"
                                        value={b.pinLabel}
                                        onChange={e => updateBuilding(b.id, { pinLabel: e.target.value })}
                                        onClick={e => e.stopPropagation()}
                                        className="w-6 h-6 text-center text-xs font-bold bg-black border border-zinc-700 rounded text-white focus:outline-none focus:border-indigo-500"
                                    />
                                    <span className="flex-1 text-sm font-medium truncate text-zinc-200">
                                        {b.name || 'Unnamed Feature'}
                                    </span>
                                    <button onClick={(e) => removeBuilding(b.id, e)} className="text-zinc-600 hover:text-red-400">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                                
                                {isActive && (
                                    <div className="mt-3 pt-3 border-t border-zinc-800 space-y-2">
                                        <input
                                            type="text"
                                            value={b.name}
                                            onChange={e => updateBuilding(b.id, { name: e.target.value })}
                                            placeholder="Feature Name"
                                            className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-white"
                                        />
                                        <textarea
                                            value={b.description}
                                            onChange={e => updateBuilding(b.id, { description: e.target.value })}
                                            placeholder="Description..."
                                            className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-white h-16 resize-none"
                                        />
                                        <button
                                            onClick={() => setPlacingPinId(isPlacing ? null : b.id)}
                                            className={`w-full py-1.5 rounded text-xs font-bold flex items-center justify-center gap-1.5 transition-colors ${
                                                isPlacing 
                                                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50' 
                                                : isPinned
                                                    ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
                                                    : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                                            }`}
                                        >
                                            <MapPin size={12} />
                                            {isPlacing ? 'Cancel Placement' : isPinned ? 'Move Pin' : 'Place on Map'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
